﻿import $ from 'jquery';
import { ThumbnailUpload, IThumbnailUploadOptions, IUploadCompleteResponse} from "./thumbnailupload"
import Modal from "./modal";
import Ajax from "./remote/ajax";
import Url from "../utilities/url";
import FileSize from "../utilities/filesize";
import BuildsExtensionIcon from "../utilities/buildsextensionicon";
import "../utilities/disable";
import "../utilities/readonly"
import { FileName } from '../../../rustig';

declare global {
  interface JQuery {
    asyncfilefield(option, args): any;
  }
}

export class AsyncFileField {
  public static get wireup(): string { return '[data-file-field]'; }

  private $element: JQuery;
  private options: IAsyncFileFieldOptions;

  private $fileChooser: JQuery;
  private $previewContainer: JQuery;

  private $hiddenId: JQuery;
  private $hiddenFilename: JQuery;
  private $hiddenLength: JQuery;

  private fileBeingUploaded: File;
  private state: string;
  private uploader: ThumbnailUpload;

  private onValidationComplete: any;
  private static get preUploadInvalidFileMessage(): string { return 'invalid-file'; }
  private static get cannotUploadAnymoreFiles(): string { return 'You can not upload any more files.'; }

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$hiddenId; }

  public constructor($element: JQuery, options?: IAsyncFileFieldOptions) {
    if (!$element || $element.length != 1)
      throw new Error('You must provide a single element to Rustig.AsyncFileField');
    this.$element = $element;

    this.options = $.extend({}, this.$element.data(), options);
    this.initializeElements();
    this.setOnClickListeners();
    this.initializeDocumentUploader();
    this.setState();
  }

  public addFile(file: File): void {
    if (!file)
      throw new Error('file cannot be null');
    if (this.state !== 'beforeupload')
      throw new Error('Cannot upload file');

    this.uploader.addFile(file);
  }

  public value(value?: any): any | void {
    if (value !== undefined && value.id) {
      this.setValue(value);
      this.reload();
    }
    else
      return this.getValue();
  }

  public text(text?: any): any | void {
    if (text !== undefined) {
      this.$hiddenFilename.val(text);
      this.reload();
    }
    else {
      let fileName = this.$userFacingElement.find('tu-name'.asDataSelector()).text();
      if (!fileName)
        return null;

      return new FileName(fileName).withoutExtension();
    }
  }

  public name(): string {
    return this.$hiddenId.attr('name');
  }

  private reload(): void {
    if (this.state === 'uploading')
      this.cancelUploadInProgress();
    else if (this.state === 'uploadcomplete')
      this.clearInput();

    this.setUploadCompleteState();
  }

  private getValue(): FileInputs {
    return {
      id: this.$hiddenId.val() as number,
      filename: this.$hiddenFilename.val() as string,
      length: this.$hiddenLength.val() as number
    };
  }

  private setValue(value: any): void {
    if (value["id"])
      this.$hiddenId.val(value["id"])
    if (value["filename"])
      this.$hiddenFilename.val(value["filename"])
    if (value["length"])
      this.$hiddenLength.val(value["length"])
  }

  private initializeElements(): void {
    this.$fileChooser = this.$element.find('[data-ff-file-chooser]');
    this.$previewContainer = this.$element.find('[data-ff-preview]');
    this.$hiddenId = this.$element.find('[data-ff-id]');
    this.$hiddenFilename = this.$element.find('[data-ff-filename]');
    this.$hiddenLength = this.$element.find('[data-ff-length]');
  }

  private initializeDocumentUploader(): void {
    let id = this.options.uniqueId || Math.floor(Math.random() * 100000);
    this.$element.attr('data-ff-uploader-id', id);

    this.uploader = new ThumbnailUpload(<IThumbnailUploadOptions>{
      $dropzone: this.$fileChooser,
      $previewContainer: this.$previewContainer,
      uniqueId: id,
      onFileAdded: this.onFileAdded.bind(this),
      onUploadComplete: this.onUploadComplete.bind(this),
      onError: this.onError.bind(this),
      accept: this.$fileChooser.is('[data-ff-preupload-url]') ? this.preUpload.bind(this) : null
    });

    if (this.options.dropElementSelector) {
      this.uploader.addElementAsDragAndDropArea($(this.options.dropElementSelector)[0]);
    }

    var value = this.getValue();
    if (value.id) {
      let file = new File(["temp"], value.filename, null);
      this.uploader.initFile(file);
    }
  }

  private preUpload(file: File, onValidationComplete: any): void {
    this.onValidationComplete = onValidationComplete;

    Ajax.execute({
      url: new Url(this.$fileChooser.data('ff-preupload-url'))
        .setQueryParameter('uploaderId', this.$element.data('ff-uploader-id'))
        .setQueryParameter('fileSize', file.size.toString())
        .setQueryParameter('fileName', file.name)
        .toUrl(),
      error: (jqXHR, textStatus, errorThrown) => {
        onValidationComplete(AsyncFileField.preUploadInvalidFileMessage);
        if (!window.isWindowUnloading) {
          if (jqXHR.status == 0)
            Modal.openDisconnectModal();
          else
            Modal.openErrorModal(jqXHR.responseText);
        }
      }
    });
  }

  public static preUploadComplete(data: IPreUploadResult): void {
    var asyncFileField = <AsyncFileField>$(('ff-uploader-id').asDataSelector(data.id)).data('rustig.asyncfilefield');
    if (data.fileIsValid) {
      asyncFileField.onValidationComplete();
      asyncFileField.$element.trigger('preuploaded');
    }
    else
      asyncFileField.onValidationComplete(AsyncFileField.preUploadInvalidFileMessage); // triggers an error
  }

  private setOnClickListeners(): void {
    this.$element.on('click.rustig.filefield', '[data-ff-cancel]', this.onCancel.bind(this));
    if (this.options.cancelsKey)
      $(document).on('click.rustig.filefield', '[data-ff-cancel-' + this.options.cancelsKey + ']', this.onCancel.bind(this));

    this.$fileChooser.on('click.rustig.filefield', function (e: Event) {
      if (this.$element.isReadonly() || this.$element.isDisabled())
        e.stopImmediatePropagation();
    }.bind(this));
  }

  private setState(): void {
    if (this.$hiddenId.val()) {
      this.setUploadCompleteState();
    }
    else if (this.uploader.getUploadingFiles().length) {
      this.$element.addClass('ff-uploading');
      this.state = 'uploading';
      this.fileBeingUploaded = this.uploader.getUploadingFiles()[0];
    }
    else {
      this.state = 'beforeupload';
    }
  }

  private setUploadCompleteState(): void {
    this.$element.addClass('ff-upload-complete');
    this.state = 'uploadcomplete';
    this.createThumbnail();
    this.fileBeingUploaded = this.uploader.getAllFiles()[0];
  }

  private createThumbnail(): void {
    let $thumbnail = this.$element.find('[data-tu-preview-template]').children().first().clone().appendTo(this.$previewContainer);

    let filename = this.$hiddenFilename.val() as string;
    let filesize = Number(this.$hiddenLength.val());
    $thumbnail.find('[data-tu-name]').attr('title', filename).html(filename);
    $thumbnail.find('[data-ff-filetype]').addClass(new BuildsExtensionIcon().execute(filename));
    $thumbnail.find('[data-ff-filesize]').html(new FileSize(filesize).toString());
  }

  private onCancel(): void {
    if (this.$element.isReadonly() || this.$element.isDisabled())
      return;

    if (this.state === 'uploading')
      this.cancelUploadInProgress();
    else if (this.state === 'uploadcomplete')
      this.cancelCompletedUpload();

    this.$element.trigger('fileremoved');
  }

  private cancelUploadInProgress(): void {
    this.$element.removeClass('ff-uploading');
    this.state = 'beforeupload';
    this.enableLinkedElements();
    this.uploader.removeFile(this.fileBeingUploaded);
  }

  private cancelCompletedUpload(): void {
    Ajax.execute({
      url: this.buildsRemoveUrl(),
      type: 'post',
      success: () => {
        this.uploader.removeFile(this.fileBeingUploaded);
        this.clearInput();
      }
    });
  }

  private clearInput(): void {
    this.state = 'beforeupload';
    this.$previewContainer.html('');
    this.$element.removeClass('ff-upload-complete');
    this.$hiddenId.val(null);
    this.$hiddenFilename.val(null);
    this.$hiddenLength.val(null);
  }

  private buildsRemoveUrl(): string {
    let url = this.$fileChooser.data('tu-remove-url');
    let serverId = this.$hiddenId.val() as string;

    return new Url(url)
      .setQueryParameter('serverId', serverId)
      .toUrl();
  }

  private onFileAdded(file: File): void {
    this.$element.addClass('ff-uploading');
    this.state = 'uploading';
    this.fileBeingUploaded = file;

    this.disableLinkedElements();

    let $thumbnail = $(file.previewElement);
    $thumbnail.find('[data-ff-filetype]').addClass(new BuildsExtensionIcon().execute(file.name));
    $thumbnail.find('[data-ff-filesize]').html(new FileSize(file.size).toString());
    this.$element.trigger('fileadded');
  }

  private onUploadComplete(file: File, responseText: IUploadCompleteResponse[], xhr: JQueryXHR): void {
    if (!responseText || !responseText.length || responseText[0].errorText)
      return;

    this.$element.removeClass('ff-uploading').addClass('ff-upload-complete');
    this.state = 'uploadcomplete';

    let serverId = $(this.fileBeingUploaded.previewElement).data('server-id');
    this.$hiddenId.val(serverId);
    this.$hiddenFilename.val(this.fileBeingUploaded.name);
    this.$hiddenLength.val(this.fileBeingUploaded.size);

    this.enableLinkedElements();
  }

  private enableLinkedElements(): void {
    if (!this.options.disablesKey)
      return;

    $('[data-ff-toggle-enabled-' + this.options.disablesKey + ']').each(function () {
      let $element = $(this);
      let numberOfFilesUploading = $element.data('async-file-field-uploading-count') - 1;
      $element.data('async-file-field-uploading-count', numberOfFilesUploading);
      if (numberOfFilesUploading == 0)
        $element.enable();
    });
  }

  private disableLinkedElements(): void {
    if (!this.options.disablesKey)
      return;

    $('[data-ff-toggle-enabled-' + this.options.disablesKey + ']').each(function () {
      let $element = $(this);
      let numberOfFilesUploading = $element.data('async-file-field-uploading-count') || 0;
      $element.data('async-file-field-uploading-count', numberOfFilesUploading + 1)
        .disable();
    });
  }

  private onError(file: File, errorMessage: string, xhr: JQueryXHR): void {
    if (errorMessage !== AsyncFileField.preUploadInvalidFileMessage)
      Modal.openErrorModal(errorMessage);

    if (errorMessage === AsyncFileField.cannotUploadAnymoreFiles) {
      this.$previewContainer.find('.dz-error').remove();
      this.state = 'uploadcomplete';
      this.fileBeingUploaded = this.uploader.getAllFiles()[0];
    }
    else {
      this.state = 'beforeupload';      
      this.$previewContainer.html('');
    }

    this.$element.removeClass('ff-uploading');
    this.enableLinkedElements();
    this.uploader.removeFile(file);
    this.$element.trigger('fileremoved');
  }


}

interface FileInputs {
  id?: number;
  filename?: string;
  length?: number;
}

export interface IPreUploadResult {
  id: number;
  fileIsValid: boolean;
}

export interface IAsyncFileFieldOptions {
  disablesKey?: string;
  cancelsKey?: string;
  uniqueId?: number;
  dropElementSelector?: string;
}

function Plugin(option, args) {
  return this.each(function () {
    var $this = $(this);
    var data = $this.data('rustig.asyncfilefield');
    var options = typeof option == 'object' && option;

    if (!data) {
      data = new AsyncFileField($this, options);
      $this.data('rustig.asyncfilefield', data);
    }
    if (typeof option == 'string')
      data[option](args);
  });
}

$.fn.asyncfilefield = Plugin;

export default AsyncFileField;
